import React, { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export type CollapsibleVariant = 'default' | 'card' | 'bordered' | 'minimal';
export type IconPosition = 'left' | 'right';
export type AnimationDuration = 'fast' | 'normal' | 'slow';

export interface CollapsibleSectionProps {
  children: ReactNode;
  title: string | ReactNode;
  subtitle?: string;
  defaultExpanded?: boolean;
  variant?: CollapsibleVariant;
  iconPosition?: IconPosition;
  customIcon?: ReactNode;
  disabled?: boolean;
  persistState?: boolean;
  storageKey?: string;
  animationDuration?: AnimationDuration;
  onToggle?: (expanded: boolean) => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  badge?: string | number;
  actions?: ReactNode; // Additional actions in header
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  children,
  title,
  subtitle,
  defaultExpanded = false,
  variant = 'default',
  iconPosition = 'right',
  customIcon,
  disabled = false,
  persistState = false,
  storageKey,
  animationDuration = 'normal',
  onToggle,
  className = '',
  headerClassName = '',
  contentClassName = '',
  badge,
  actions,
}) => {
  const { classes, isCompact, isNormal } = useDensity();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>(0);

  // Load persisted state
  useEffect(() => {
    if (persistState && storageKey) {
      const saved = localStorage.getItem(`collapsible-${storageKey}`);
      if (saved !== null) {
        setIsExpanded(saved === 'true');
      }
    }
  }, [persistState, storageKey]);

  // Animation duration mapping
  const getDurationMs = useCallback(() => {
    const durations = {
      fast: 150,
      normal: 300,
      slow: 500,
    };
    return durations[animationDuration];
  }, [animationDuration]);

  // Update content height when expanded state changes
  useEffect(() => {
    if (!contentRef.current) return;

    if (isExpanded) {
      const scrollHeight = contentRef.current.scrollHeight;
      setContentHeight(scrollHeight);

      // Set to auto after animation completes
      const timeout = setTimeout(() => {
        setContentHeight('auto');
      }, getDurationMs());

      return () => clearTimeout(timeout);
    } else {
      setContentHeight(contentRef.current.scrollHeight);
      // Force reflow
      void contentRef.current.offsetHeight;
      setContentHeight(0);
    }
  }, [isExpanded, children, getDurationMs]);

  const getDurationClass = () => {
    const classes = {
      fast: 'duration-150',
      normal: 'duration-300',
      slow: 'duration-500',
    };
    return classes[animationDuration];
  };

  // Toggle expanded state
  const toggle = (event?: React.MouseEvent) => {
    if (disabled) return;

    // Don't toggle if clicking on interactive elements (buttons, inputs, etc.)
    if (event && event.target) {
      const target = event.target as HTMLElement;
      const interactiveElements = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'A'];
      const isInteractiveElement = interactiveElements.includes(target.tagName);
      const hasInteractiveParent = target.closest(
        'button, input, select, textarea, a, [role="button"], [role="listbox"]'
      );
      const isInSearchableSelect = target.closest('.searchable-select-container');

      if (isInteractiveElement || hasInteractiveParent || isInSearchableSelect) {
        return; // Don't toggle the section
      }
    }

    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    // Persist state if enabled
    if (persistState && storageKey) {
      localStorage.setItem(`collapsible-${storageKey}`, newExpanded.toString());
    }

    // Call callback
    onToggle?.(newExpanded);
  };

  // Variant-specific styling
  const getVariantClasses = () => {
    const baseClasses = 'collapsible-section';

    switch (variant) {
      case 'card':
        return classes(
          [
            baseClasses,
            'bg-white rounded-lg shadow border border-gray-200',
            isCompact ? 'p-2' : isNormal ? 'p-3' : 'p-4',
          ]
            .filter(Boolean)
            .join(' ')
        );

      case 'bordered':
        return classes(
          [
            baseClasses,
            'border border-gray-200 rounded-md',
            isCompact ? 'p-2' : isNormal ? 'p-3' : 'p-4',
          ]
            .filter(Boolean)
            .join(' ')
        );

      case 'minimal':
        return classes(
          [baseClasses, 'border-b border-gray-100 last:border-b-0'].filter(Boolean).join(' ')
        );

      case 'default':
      default:
        return classes(
          [baseClasses, 'bg-gray-50 rounded-md', isCompact ? 'p-2' : isNormal ? 'p-3' : 'p-4']
            .filter(Boolean)
            .join(' ')
        );
    }
  };

  // Header styling
  const headerClasses = classes(
    [
      'flex items-center cursor-pointer select-none transition-colors',
      disabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-600',
      variant === 'minimal' ? (isCompact ? 'py-2' : isNormal ? 'py-3' : 'py-4') : '',
      headerClassName,
    ]
      .filter(Boolean)
      .join(' ')
  );

  // Title styling
  const titleClasses = classes(
    [
      'font-semibold text-gray-900',
      isCompact ? 'text-xs' : isNormal ? 'text-sm' : 'text-base',
      iconPosition === 'left' ? 'ml-3' : 'mr-3',
    ]
      .filter(Boolean)
      .join(' ')
  );

  const subtitleClasses = classes(
    ['text-gray-500', isCompact ? 'text-xs' : 'text-sm'].filter(Boolean).join(' ')
  );

  // Icon styling
  const iconClasses = classes(
    [
      'transition-transform',
      getDurationClass(),
      isExpanded ? 'rotate-180' : 'rotate-0',
      disabled ? 'text-gray-300' : 'text-gray-400',
    ]
      .filter(Boolean)
      .join(' ')
  );

  // Content styling
  const contentWrapperClasses = classes(
    ['overflow-hidden transition-all', getDurationClass(), contentClassName]
      .filter(Boolean)
      .join(' ')
  );

  const contentInnerClasses = classes(
    variant === 'minimal' ? '' : isCompact ? 'pt-2' : isNormal ? 'pt-3' : 'pt-4'
  );

  // Badge styling
  const badgeClasses = classes(
    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2'
  );

  // Default chevron icon
  const defaultIcon = (
    <svg
      className={classes(['w-4 h-4', iconClasses].filter(Boolean).join(' '))}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const icon = customIcon ? <div className={iconClasses}>{customIcon}</div> : defaultIcon;

  return (
    <div className={classes([getVariantClasses(), className].filter(Boolean).join(' '))}>
      {/* Header */}
      <div
        className={headerClasses}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isExpanded}
        aria-disabled={disabled}
      >
        {/* Icon on left */}
        {iconPosition === 'left' && icon}

        {/* Title and subtitle */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <div className={titleClasses}>
              {typeof title === 'string' ? <span>{title}</span> : title}
            </div>

            {/* Badge */}
            {badge && <span className={badgeClasses}>{badge}</span>}
          </div>

          {subtitle && <div className={subtitleClasses}>{subtitle}</div>}
        </div>

        {/* Actions */}
        {actions && (
          <div
            className="flex items-center space-x-2 mr-3"
            onClick={(e) => e.stopPropagation()} // Prevent toggle when clicking actions
          >
            {actions}
          </div>
        )}

        {/* Icon on right */}
        {iconPosition === 'right' && icon}
      </div>

      {/* Content */}
      <div
        className={contentWrapperClasses}
        style={{
          height: isExpanded ? contentHeight : 0,
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div ref={contentRef} className={contentInnerClasses}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Preset configurations for common use cases
export const CollapsiblePresets = {
  // Advanced form options
  formAdvanced: {
    variant: 'bordered' as CollapsibleVariant,
    defaultExpanded: false,
    persistState: true,
    animationDuration: 'normal' as AnimationDuration,
  },

  // Settings categories
  settingsCategory: {
    variant: 'card' as CollapsibleVariant,
    defaultExpanded: true,
    persistState: true,
    animationDuration: 'fast' as AnimationDuration,
  },

  // FAQ items
  faqItem: {
    variant: 'minimal' as CollapsibleVariant,
    defaultExpanded: false,
    animationDuration: 'normal' as AnimationDuration,
  },

  // Dashboard widgets
  dashboardWidget: {
    variant: 'card' as CollapsibleVariant,
    defaultExpanded: true,
    persistState: true,
    animationDuration: 'fast' as AnimationDuration,
  },

  // Filter sections
  filterSection: {
    variant: 'default' as CollapsibleVariant,
    defaultExpanded: false,
    persistState: true,
    animationDuration: 'fast' as AnimationDuration,
    iconPosition: 'left' as IconPosition,
  },

  // Help sections
  helpSection: {
    variant: 'minimal' as CollapsibleVariant,
    defaultExpanded: false,
    animationDuration: 'normal' as AnimationDuration,
  },
};

export default CollapsibleSection;
